import {useContext, useEffect, useState} from "react"
import {useParams} from "react-router-dom"
import {InputText} from "primereact/inputtext"
import {InputTextarea} from "primereact/inputtextarea"
import {Button} from "primereact/button"
import {Dropdown} from "primereact/dropdown"
import {Badge} from "primereact/badge"
import {Divider} from "primereact/divider"
import moment from "moment"
import {useImmer} from "use-immer"
import api from "../../../api"
import userContext from "../../../context/userContext"
import getTaskStateSeverity from "../../../function/getTaskStateSeverity"
import useTaskStates from "../../../hook/useTaskStates"
import getSubTaskStateName from "../../../function/getSubTaskStateName"
import useSubTaskCauses from "../../../hook/useSubTaskCauses"
import commonStyles from "../../common.module.css"

export default function TaskPreventionSubTaskForm() {
  const {uuid} = useParams()
  const back = () => window.history.back()
  const user = useContext(userContext)
  const taskStates = useTaskStates()
  const [task, setTask] = useState({})
  const [refresher, setRefresher] = useState(true)

  const subTaskCauses = useSubTaskCauses()
  const [subTaskCause, setSubTaskCause] = useState("")
  const handleSubTaskCauseChange = (e) => setSubTaskCause(e.target.value)
  const getSubTaskCauseName = (value) => {
    const cause = subTaskCauses.find((cause) => cause.value === value)
    return cause ? cause.name : ""
  }
  const [expandedSubTaskUuid, setExpandedSubTaskUuid] = useState("")
  const [reportName, setReportName] = useState("")
  const handleReportNameChange = (e) => setReportName(e.target.value)
  const [reportDescription, setReportDescription] = useState("")
  const handleReportDescriptionChange = (e) => setReportDescription(e.target.value)
  const expandSubTask = (subTask) => {
    if (subTask.uuid === expandedSubTaskUuid) {
      setExpandedSubTaskUuid("")
      setReportName("")
      setReportDescription("")
    } else {
      setExpandedSubTaskUuid(subTask.uuid)
      setReportName(`Nelze dokončit: ${subTask.description}`)
      setReportDescription("")
    }
  }

  const [subTasks, setSubTasks] = useImmer([])
  const handleSubTaskChange = (subTask, key, value) => {
    setSubTasks((draft) => {
      const draftSubTask = draft.find((draftSubTask) => draftSubTask.uuid === subTask.uuid)

      if (key === "value") {
        value = parseFloat(value)
      }

      draftSubTask[key] = value
    })
  }

  const updateSubtask = async (subTask, state) => {
    await api.post(`/tasks/${uuid}`, {uuid, state: "IN_PROGRESS"})

    const params = {
      uuid: subTask.uuid, state, state_description: subTask.state_description
    }

    if (state === "NOT_FINISHED") {
      params.cause_of_unfinished = subTaskCause
    }

    if (subTask.value) {
      params.value = subTask.value
    }

    api.post(`/tasks/${uuid}/sub/${subTask.uuid}`, params)
      .then(() => {
        setRefresher(!refresher)
        setSubTaskCause("")
        setExpandedSubTaskUuid("")
      })
  }

  const createReport = async (subTask) => {
    await api.put("/tasks", {
      type: "REPORT",
      operation: user.user.operation.uuid,
      team: task.team.uuid,
      machine: task.machine.uuid,
      starts_at: moment().toISOString(), // now
      name: reportName,
      description: reportDescription,
      files: [],
    })
    await updateSubtask(subTask, "NOT_FINISHED")
  }

  const getTaskStateName = () => taskStates.length && task.state
    ? taskStates.find((taskState) => taskState.value === task.state).name
    : ""

  const getSubTaskBadgeValue = (subTask) => {
    const subTaskStateName = getSubTaskStateName(subTask.state)
    const subTaskCauseName = getSubTaskCauseName(subTask.cause_of_unfinished)
    if (subTaskCauseName) {
      return `${subTaskStateName} (${subTaskCauseName})`
    }

    return subTaskStateName
  }

  useEffect(() => {
    if (uuid) {
      api.get(`/tasks/${uuid}`)
        .then(({data}) => {
          setTask(data.data)
          setSubTasks((draft) => {
            draft.splice(0, draft.length)
            data.data.tasks.forEach((subTask) => draft.push(subTask))
          })
        })
    }
  }, [uuid, refresher, setSubTasks])

  return (
    <userContext.Consumer>
      {({hasPerm}) => (
        <div className={"col-12 md:col-6"}>
          <div className={"p-fluid"}>
            <div className={"field"}>
              <label>Aktuální stav pravidelné údržby:</label>
              <InputText value={getTaskStateName()} disabled={true}/>
            </div>

            <div className={"field"}>
              <label>Název:</label>
              <InputText value={task.name || ""} disabled={true}/>
            </div>

            <div className={"field"}>
              <label>Detailní popis:</label>
              <InputTextarea value={task.description || ""} disabled={true}/>
            </div>

            {subTasks.map((subTask, i) => (
              <div key={subTask.uuid}>
                <Divider>
                  <Badge className={"mr-1"}
                         value={getSubTaskBadgeValue(subTask)}
                         severity={getTaskStateSeverity(subTask.state)}/>
                  <label>Podúkol č. {i + 1}: <i className={"my-2"}>{subTask.description}</i></label>
                </Divider>

                {subTask.value_description && (
                  <div className={"field"}>
                    <label className={commonStyles.labelRequired}>
                      {subTask.value_description} ({subTask.value_unit})
                    </label>
                    <InputText placeholder={subTask.value_description} type={"number"}
                               value={subTask.value || ""} disabled={subTask.state !== "UNKNOWN"}
                               onChange={(e) => handleSubTaskChange(subTask, "value", e.target.value)}/>
                  </div>
                )}

                <div className={"field"}>
                  <label>Poznámka:</label>
                  <div className={"p-inputgroup"}>
                    <InputText value={subTask.state_description || ""}
                               placeholder={`Poznámka k úkolu č. ${i + 1}`}
                               disabled={subTask.state !== "UNKNOWN"}
                               onChange={(e) => handleSubTaskChange(subTask, "state_description", e.target.value)}/>

                    {subTask.state === "UNKNOWN" && (
                      <>
                        <Button type={"button"}
                                className={"p-button-success"} icon={"pi pi-check"}
                                onClick={() => updateSubtask(subTask, "FINISHED")}
                                disabled={(subTask.value_description && !subTask.value) || !hasPerm("ROLE_PERM_PREVENTION_WRITE")}/>
                        <Button type={"button"}
                                className={"p-button-danger"} icon={"pi pi-times"}
                                onClick={() => expandSubTask(subTask)}
                                disabled={!hasPerm("ROLE_PERM_PREVENTION_WRITE")}
                        />
                      </>
                    )}
                  </div>

                  {subTask.uuid === expandedSubTaskUuid && (
                    <>
                      <Divider>Důvod nedokončení</Divider>

                      <div className={"field"}>
                        <label className={commonStyles.labelRequired}>Kategorie:</label>
                        <Dropdown options={subTaskCauses} value={subTaskCause}
                                  onChange={handleSubTaskCauseChange} optionLabel={"name"}
                                  optionValue={"value"} placeholder={"Kategorie"}
                                  disabled={subTask.state === "NOT_FINISHED"}/>
                      </div>

                      <Divider>Hlášení</Divider>

                      <div className={"field"}>
                        <label className={commonStyles.labelRequired}>Název:</label>
                        <InputText value={reportName} placeholder={"Název"}
                                   onChange={handleReportNameChange}/>
                      </div>
                      <div className={"field"}>
                        <label className={commonStyles.labelRequired}>Popis:</label>
                        <InputText value={reportDescription} placeholder={"Popis"}
                                   onChange={handleReportDescriptionChange}/>
                      </div>

                      <Button type={"button"} className={"p-button-danger"}
                              label={"Dokončit podúkol a vytvořit hlášení"}
                              disabled={!subTaskCause || !reportName || !reportDescription}
                              onClick={() => createReport(subTask)}/>
                    </>
                  )}
                </div>
              </div>
            ))}
          </div>

          <div className={"flex"}>
            <Button type={"button"} label={"Zpět"} className={"p-button-warning m-1"}
                    onClick={back}/>
          </div>
        </div>
      )}
    </userContext.Consumer>
  )
}
